import {
  useBreakpointValue,
  Stack,
  Box,
  Heading,
  HStack,
  Button,
  Container,
  Text,
} from '@chakra-ui/react'
import { Logo } from '@components/Elements/Logo'
import { PasswordInput } from '@components/Forms/Inputs/PasswordInput'
import { FormWrapper } from '@components/Forms/Wrappers/FormWrapper'
import useCmxToast from '@hooks/useCmxToast'
import { firebaseClientInitConfig } from '@lib/firebase/config'
import tenantManager from '@lib/firebase/tenantManager'
import {
  createFileRoute,
  useNavigate,
  useSearch,
  Link,
} from '@tanstack/react-router'
import { AuthErrorCodes } from 'firebase/auth'
import { Form } from '@components/Forms/Form'
import { useState } from 'react'
import { Route as SignInRoute } from '@routes/signin'
import { Route as ForgotPasswordRoute } from '@routes/forgotPassword'
import { Route as StaffForgotPasswordRoute } from '@routes/staff_.forgotPassword'
import { Route as StaffSignInRoute } from '@routes/staff_.signin'
import { z } from 'zod'
import { useUser } from '@lib/firebase/provider'

const formSchema = z
  .object({
    password: z.string().min(8),
    confirmPassword: z.string().min(8),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'The passwords do not match',
    path: ['confirmPassword'],
  })

type FormSchema = z.infer<typeof formSchema>

const ResetPassword = () => {
  const { resetPassword } = useUser()
  const navigate = useNavigate()
  const search = useSearch({ from: '/resetPassword' })
  const toast = useCmxToast()
  const headingBreakPoints = useBreakpointValue({ base: 'xs', md: 'sm' })
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isCodeExpired, setIsCodeExpired] = useState<boolean>(false)
  const { oobCode, tenantId } = search

  const isStaff = tenantManager.isCurrentTenant(tenantId as string)

  const handleSubmit = async (formData: FormSchema) => {
    setIsSubmitting(true)
    await tenantManager.setTenantId(tenantId as string)

    try {
      await resetPassword(oobCode, formData.password)
      toast({ title: 'Password changed', status: 'success', duration: 3000 })
      setIsSubmitting(false)

      navigate({
        to: tenantManager.isCurrentTenant(
          firebaseClientInitConfig.tenantIds.staff
        )
          ? StaffSignInRoute.to
          : SignInRoute.to,
      })
    } catch (error) {
      setIsSubmitting(false)
      if (error.code === AuthErrorCodes.EXPIRED_OOB_CODE) {
        setIsCodeExpired(true)
      } else {
        toast({
          title: 'Unable to change password',
          description: error.message,
          status: 'error',
        })
      }
    }
  }

  if (isCodeExpired || !oobCode) {
    return (
      <Container maxW="md" py={{ base: '12', md: '24' }}>
        <Stack spacing="8">
          <Stack spacing="6">
            <Box px="120" py="50" marginTop="30px">
              <Logo />
            </Box>
            <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
              <Heading size={headingBreakPoints}>Code Expired</Heading>
              <HStack spacing="1" justify="center">
                <Text color="muted">
                  We'll send you a new reset password email, click{' '}
                  <Link
                    to={
                      isStaff
                        ? StaffForgotPasswordRoute.to
                        : ForgotPasswordRoute.to
                    }
                  >
                    <Button variant={'link'} color={'blue.200'}>
                      here
                    </Button>
                  </Link>
                </Text>
              </HStack>
            </Stack>
          </Stack>
        </Stack>
      </Container>
    )
  }

  return (
    <Container maxW="md" py={{ base: '12', md: '24' }}>
      <Stack spacing="8">
        <Stack spacing="6">
          <Box px="120" py="50" marginTop="30px">
            <Logo />
          </Box>
          <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
            <Heading size={headingBreakPoints}>Reset Password</Heading>
            <HStack spacing="1" justify="center">
              <Text color="muted">Enter your new password</Text>
            </HStack>
          </Stack>
        </Stack>
        <FormWrapper formSchema={formSchema}>
          <Form onSubmit={handleSubmit}>
            <Stack spacing="6">
              <PasswordInput name={'password'} label={'Password'} />
              <PasswordInput
                name={'confirmPassword'}
                label={'Confirm Password'}
              />
              <Button
                type={'submit'}
                colorScheme={'blue'}
                variant={'solid'}
                isLoading={isSubmitting}
              >
                Change
              </Button>
            </Stack>
          </Form>
        </FormWrapper>
      </Stack>
    </Container>
  )
}

const paramsSchema = z.object({
  oobCode: z.string(),
  tenantId: z.string().optional(),
})

export const Route = createFileRoute('/resetPassword')({
  component: ResetPassword,
  validateSearch: paramsSchema,
})
